import { css } from '@emotion/react';
import { serializeStyles } from '@emotion/serialize';
import { useCallback, useEffect, useMemo } from 'react';
import { TokenHandler } from '@/modules/app/components/token-handler';
import { useEvent, useTheme } from '@/modules/common';
import { BehaviourContext } from '@/modules/context';
import { useIsWidgetDisabled } from '@/modules/widget/hooks';
import {
  bridgeAtom,
  isOpenChatAtom,
  isSemiOpenChatAtom,
  messageTimeoutAtom,
  useAtom,
  useSetValue,
  useValue,
} from '@/store';
import { APP_ID, AppEvents, WRAPPER_ELEMENT_ID } from '@/utils';
import { DataLayer } from './components';

export const App = () => {
  const { isEmbedded, closeChatWithEsc } = useValue(bridgeAtom);

  const theme = useTheme();
  const isWidgetDisabled = useIsWidgetDisabled();

  const [isOpenChat, setIsOpenChat] = useAtom(isOpenChatAtom);
  const setMessageTimeout = useSetValue(messageTimeoutAtom);
  const setIsSemiOpen = useSetValue(isSemiOpenChatAtom);

  const toggleChat = useCallback(
    (params: { isOpenChat?: boolean } = {}) => {
      setIsSemiOpen(false);
      setIsOpenChat((prev) => {
        const newState = params?.isOpenChat ?? !prev;
        const INDWrap = document.getElementById('INDWrap');

        if (INDWrap) {
          INDWrap.style.zIndex = newState ? '11' : '2147483647';
        }

        if (newState) {
          document.querySelector('.saywhatt-toggle-container.saywhatt-desktop')?.classList.add('disabled');
        } else {
          // do not send a timeout message if chat is closed
          setMessageTimeout(-1);
          document.querySelector('.saywhatt-toggle-container.saywhatt-desktop')?.classList.remove('disabled');
        }

        return newState;
      });
    },
    [setIsSemiOpen, setIsOpenChat, setMessageTimeout]
  );

  const onToggleChat = useCallback(
    (e: CustomEvent<boolean>) => {
      toggleChat({ isOpenChat: e.detail });
    },
    [toggleChat]
  );

  useEvent({ name: AppEvents.TOGGLE_CHAT, handler: onToggleChat });

  useEffect(() => {
    if (isEmbedded) {
      // embedded chat should be always open
      setIsOpenChat(true);
    }
  }, [isEmbedded, setIsOpenChat]);

  useEffect(() => {
    const $app = document.getElementById(APP_ID);
    const $wrapper = document.getElementById(WRAPPER_ELEMENT_ID);

    const isMobile = window.matchMedia(`(${theme.mediaMobile})`).matches;
    const themeKey = isMobile ? 'mobile' : 'desktop';

    if (isOpenChat && (isEmbedded || !isWidgetDisabled)) {
      if ($app) {
        $app.style.cssText += serializeStyles([css(theme.appWrapper[themeKey].open)]).styles;
      }

      if ($wrapper) {
        $wrapper.style.cssText += serializeStyles([css(theme.outerWrapper[themeKey].open)]).styles;
      }

      return;
    }

    if ($app) {
      $app.style.cssText += serializeStyles([css(theme.appWrapper[themeKey].close)]).styles;
    }

    if ($wrapper) {
      $wrapper.style.cssText += serializeStyles([css(theme.outerWrapper[themeKey].close)]).styles;
    }
  }, [isOpenChat, isEmbedded, isWidgetDisabled, theme.appWrapper, theme.mediaMobile, theme.outerWrapper]);

  useEffect(() => {
    if (closeChatWithEsc) {
      const onEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          toggleChat({ isOpenChat: false });
          e.preventDefault();
        }
      };

      document.addEventListener('keydown', onEsc);

      return () => {
        document.removeEventListener('keydown', onEsc);
      };
    }
  }, [closeChatWithEsc, toggleChat]);

  const behaviourValue = useMemo(
    () => ({
      toggleChat,
    }),
    [toggleChat]
  );

  return (
    <BehaviourContext.Provider value={behaviourValue}>
      <TokenHandler>
        <DataLayer />
      </TokenHandler>
    </BehaviourContext.Provider>
  );
};
